/* Extract fenced code blocks from a markdown response so they survive
 * a session save as their own files in state.deliverables.
 *
 * Rules:
 *  - Only blocks of length >= MIN_CHARS_FOR_DELIVERABLE (avoid persisting
 *    tiny one-liners and inline snippets the user wouldn't care about).
 *  - Filename derived from the fence info string when present
 *    (```python:hello.py → hello.py), otherwise synthesized as
 *    `<modelLabel>-<n>.<ext>` using the language → extension map.
 *  - Each extracted block is pushed into state.deliverables with
 *    kind = ext, name = generated, and the raw code lives in
 *    deliverable.text (a new field, present only on extracted blocks).
 *  - Triggered by the runner after every successful fan-out + by the
 *    synthesis module after a successful synth.
 */

import { state } from './state';

const MIN_CHARS_FOR_DELIVERABLE = 60;

// Language hint → file extension.
const LANG_TO_EXT: Record<string, string> = {
  js: 'js', javascript: 'js', mjs: 'mjs', cjs: 'cjs',
  ts: 'ts', typescript: 'ts', tsx: 'tsx', jsx: 'jsx',
  py: 'py', python: 'py',
  rb: 'rb', ruby: 'rb',
  go: 'go', rs: 'rs', rust: 'rs',
  java: 'java', kt: 'kt', kotlin: 'kt',
  cs: 'cs', csharp: 'cs',
  cpp: 'cpp', 'c++': 'cpp', cxx: 'cpp',
  c: 'c', h: 'h',
  sh: 'sh', shell: 'sh', bash: 'sh', zsh: 'sh',
  ps1: 'ps1', powershell: 'ps1',
  sql: 'sql',
  yaml: 'yaml', yml: 'yml',
  json: 'json',
  toml: 'toml',
  xml: 'xml',
  html: 'html',
  css: 'css',
  md: 'md', markdown: 'md',
  tf: 'tf', terraform: 'tf',
  dockerfile: 'Dockerfile',
};

function extFromInfo(info: string): { ext: string; explicitName?: string } {
  // Common patterns: ```py / ```python / ```typescript:src/foo.ts / ```py title=run.py
  if (!info) return { ext: 'txt' };
  // Explicit name: ```lang:filename.ext  OR  ```lang title=filename.ext
  const colonMatch = info.match(/^([A-Za-z0-9+_-]+)\s*[:]\s*(\S+)$/);
  if (colonMatch) {
    const lang = colonMatch[1].toLowerCase();
    return { ext: LANG_TO_EXT[lang] || lang, explicitName: colonMatch[2] };
  }
  const titleMatch = info.match(/title\s*=\s*"?([^"\s]+)"?/i);
  if (titleMatch) {
    const lang = info.split(/\s/)[0].toLowerCase();
    return { ext: LANG_TO_EXT[lang] || lang, explicitName: titleMatch[1] };
  }
  const lang = info.trim().split(/\s/)[0].toLowerCase();
  return { ext: LANG_TO_EXT[lang] || (lang || 'txt') };
}

function* iterCodeBlocks(md: string): Generator<{ info: string; code: string }> {
  // Match BOTH ``` and ~~~ fences. Same fence char + length to close.
  const re = /(^|\n)([ \t]*)(`{3,}|~{3,})[ \t]*([^\n]*)\n([\s\S]*?)\n\2\3[ \t]*(?=\n|$)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(md)) !== null) {
    yield { info: (m[4] || '').trim(), code: m[5] };
  }
}

/* Walk a markdown blob, push every meaty code block into state.deliverables.
 * Pass a sourceLabel (model name or "synth") to disambiguate generated names. */
export function extractCodeBlocks(md: string, sourceLabel: string): number {
  if (!md) return 0;
  let n = 0;
  let counter = 1;
  for (const { info, code } of iterCodeBlocks(md)) {
    if ((code || '').length < MIN_CHARS_FOR_DELIVERABLE) continue;
    const { ext, explicitName } = extFromInfo(info);
    const name = explicitName ||
      `${sourceLabel.replace(/[^A-Za-z0-9-]+/g, '-').toLowerCase()}-block-${counter}.${ext}`;
    counter++;
    (state as any).deliverables.push({
      kind: ext,
      name,
      title: explicitName || `Code block from ${sourceLabel}`,
      text: code,                       // inline content for snapshot persistence
      source: 'response',               // distinguishes from user-export deliverables
      created_at: new Date().toISOString(),
    });
    n++;
  }
  return n;
}
